import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, Renderer2, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import _ from 'lodash';

@Component({
    selector: 'app-signature-pad',
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.less']
})
export class SignaturePadComponent implements AfterViewInit {
    @Input() height = 200; // Default height
    @Input() backendUrl = ''; // URL to send the signature
    @Input() minLength = 100; // Minimum path length for validation
    @Input() minTime = 2000; // Minimum drawing time (in milliseconds) for validation
    @Input() minBoundingBoxSize = 50; // Minimum size of the bounding box for validation
    @Output() signatureSaved = new EventEmitter<string>(); // Emits the image URL/base64 when saved
    @Output() onValidateSignature = new EventEmitter<boolean>(); // Emits the image URL/base64 when saved
    @Output() onClear = new EventEmitter<any>(); // Emits the image URL/base64 when saved
    @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    private allPaths: { x: number; y: number }[][] = []; // Stores all paths

    private _ctx!: CanvasRenderingContext2D;
    get ctx(): CanvasRenderingContext2D {
        if (!this._ctx)  {
            const canvas = this.canvasRef.nativeElement;
            this._ctx = canvas.getContext('2d') ?? null;
        }

        return this._ctx;
    }

    set ctx(value: CanvasRenderingContext2D) {
        this._ctx = value;
    }

    private isValidSignature = false;
    private drawing = false;
    private points: { x: number; y: number }[] = [];
    private startTime: number | null = null;

    constructor(private renderer: Renderer2, private http: HttpClient) {}

    ngAfterViewInit(): void {
        const canvas = this.canvasRef.nativeElement;
        this.ctx = canvas.getContext('2d') ?? null;
        this.setupCanvas();
    }

    private setupCanvas(): void {
        const canvas = this.canvasRef.nativeElement;
        const parentWidth = this.canvasRef.nativeElement.parentElement?.offsetWidth || 0;
        const devicePixelRatio = window.devicePixelRatio || 1;

        // Resize and style canvas
        this.renderer.setAttribute(canvas, 'width', `${parentWidth * devicePixelRatio}`);
        this.renderer.setAttribute(canvas, 'height', `${this.height * devicePixelRatio}`);
        this.renderer.setStyle(canvas, 'width', `${parentWidth}px`);
        this.renderer.setStyle(canvas, 'height', `${this.height}px`);

        // Scale drawing context for high-DPI devices
        this.ctx.scale(devicePixelRatio, devicePixelRatio);
        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.strokeStyle = '#000';
    }

    startDrawing(event: PointerEvent): void {
        event.preventDefault();
        this.drawing = true;
        this.startTime = this.startTime ?? Date.now(); // Set start time on first draw
        this.points = []; // Reset points for new drawing
        const pos = this.getCanvasPosition(event);
        this.ctx.beginPath();
        this.ctx.moveTo(pos.x, pos.y);
        this.points.push(pos);
    }

    draw(event: PointerEvent): void {
        if (!this.drawing) {
            return;
        }

        const pos = this.getCanvasPosition(event);
        this.ctx.lineWidth = (event.pressure || 0.5) * 4; // Adjust line width based on pressure
        this.ctx.lineTo(pos.x, pos.y);
        this.ctx.stroke();
        this.points.push(pos); // Track drawing points

        //this.drawBoundingBox(); // Update bounding box in real-time
    }

    // Calculate the bounding box for the path
    private calculateBoundingBox(): { x: number; y: number; width: number; height: number } {
        if (this.points.length === 0) {
            return { x: 0, y: 0, width: 0, height: 0 };
        }

        const xs = this.points.map((p) => p.x);
        const ys = this.points.map((p) => p.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const width = Math.max(...xs) - x;
        const height = Math.max(...ys) - y;

        return { x, y, width, height };
    }

    // Draw the bounding box on the canvas
    private drawBoundingBox(): void {
        const boundingBox = this.calculateBoundingBox();

        this.ctx.save(); // Save current context state
        this.ctx.strokeStyle = 'red';
        this.ctx.lineWidth = 1;
        this.ctx.setLineDash([5, 5]); // Dashed line for bounding box
        this.ctx.strokeRect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height);
        this.ctx.restore(); // Restore context state
    }

    stopDrawing(): void {
        if (this.drawing) {
            this.drawing = false;
            this.ctx.closePath();
            this.allPaths.push([...this.points]);

            // Perform validation after stopping drawing
            this.validateSignature();
            this.saveSignature();
        }
    }

    // Calculate the bounding box for all paths
    private calculateBoundingBoxForAllPaths(): { x: number; y: number; width: number; height: number } {
        if (this.allPaths.length === 0) {
            return { x: 0, y: 0, width: 0, height: 0 };
        }

        const xs = this.allPaths.reduce((acc, path) => acc.concat(path), []).map((p) => p.x); // Flatten all paths and extract x-coordinates
        const ys = this.allPaths.reduce((acc, path) => acc.concat(path), []).map((p) => p.y); // Flatten all paths and extract x-coordinates
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const width = Math.max(...xs) - x;
        const height = Math.max(...ys) - y;

        return { x, y, width, height };
    }

    // Draw the bounding box for all paths
    drawBoundingBoxForAllPaths(): void {
        const boundingBox = this.calculateBoundingBoxForAllPaths();

        this.ctx.save(); // Save current context state
        this.ctx.strokeStyle = 'blue';
        this.ctx.lineWidth = 1;
        this.ctx.setLineDash([5, 5]); // Dashed line for bounding box
        this.ctx.strokeRect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height);
        this.ctx.restore(); // Restore context state

        this.showPathAndBoundingBoxDetails();
    }

    clearCanvas(): void {
        const canvas = this.canvasRef.nativeElement;
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.points = [];
        this.startTime = null;
        this.allPaths = [];

        this.isValidSignature = false;
        this.onClear.emit();
    }

    getSignatureImage(): string {
        return this.canvasRef.nativeElement.toDataURL('image/png');
    }

    saveSignature(): void {
        const signatureImage = this.getSignatureImage();
        this.signatureSaved.emit(signatureImage);
    }

    private getCanvasPosition(event: PointerEvent): { x: number; y: number } {
        const rect = this.canvasRef.nativeElement.getBoundingClientRect();
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };
    }

    // Calculate total path length
    private calculatePathLength(): number {
        let length = 0;
        for (let i = 1; i < this.points.length; i++) {
            const dx = this.points[i].x - this.points[i - 1].x;
            const dy = this.points[i].y - this.points[i - 1].y;
            length += Math.sqrt(dx * dx + dy * dy);
        }
        return length;
    }

    // Calculate the total length of all paths
    private calculateTotalPathLength(): number {
        return this.allPaths.reduce((totalLength, path) => {
            for (let i = 1; i < path.length; i++) {
                const dx = path[i].x - path[i - 1].x;
                const dy = path[i].y - path[i - 1].y;
                totalLength += Math.sqrt(dx * dx + dy * dy);
            }
            return totalLength;
        }, 0);
    }

    // Show path length and bounding box details
    public showPathAndBoundingBoxDetails(): void {
        if (this.allPaths.length === 0) {
            console.warn('No paths drawn yet.');
            return;
        }

        // Calculate total path length
        const totalPathLength = this.calculateTotalPathLength();

        // Calculate bounding box dimensions
        const boundingBox = this.calculateBoundingBoxForAllPaths();


        // Calculate diagonal of the bounding box using the Pythagorean theorem
        const diagonal = Math.sqrt(Math.pow(boundingBox.width, 2) + Math.pow(boundingBox.height, 2));

        const factor = totalPathLength / diagonal;

        // Log the details
        console.log(`Total Path Length: ${totalPathLength.toFixed(2)}px`);
        console.log(`Bounding Box Height: ${boundingBox.height}px`);
        console.log(`Bounding Box Diagonal: ${diagonal.toFixed(2)}px`);
        console.log(`Diagonal factor: ${factor.toFixed(2)}`);
    }

    get isSignatureValid(): boolean {
        return !this.isValidSignature && !_.isEmpty(this.points);
    }

    // Validate the signature
    private validateSignature(): void {
        const pathLength = this.calculatePathLength();
        const timeSpent = this.startTime ? Date.now() - this.startTime : 0;
        const boundingBox = this.calculateBoundingBoxForAllPaths();

        // Calculate total path length
        const totalPathLength = this.calculateTotalPathLength();
        // Calculate diagonal of the bounding box using the Pythagorean theorem
        const diagonal = Math.sqrt(Math.pow(boundingBox.width, 2) + Math.pow(boundingBox.height, 2));
        const factor = totalPathLength / diagonal;
        if (factor >= 2 && factor <= 6) {
            this.isValidSignature = true;
        } else {
            this.isValidSignature = false;
        }

        // if (pathLength < this.minLength) {
        //     //console.log(`Signature is too short! Minimum length is ${this.minLength}px.`);
        //     this.onValidateSignature.emit(false);
        // }
        //
        // if (timeSpent < this.minTime) {
        //     //console.log(`Signature was too quick! Minimum time is ${this.minTime / 1000} seconds.`);
        //     this.onValidateSignature.emit(false);
        // }
        //
        // if (boundingBox.width < this.minBoundingBoxSize || boundingBox.height < this.minBoundingBoxSize) {
        //     //console.log(`Signature bounding box is too small! Minimum size is ${this.minBoundingBoxSize}px x ${this.minBoundingBoxSize}px.`;
        //     this.onValidateSignature.emit(false);
        // }
        //
        this.onValidateSignature.emit(this.isValidSignature);
    }
}

